import { ICostAnalysisState } from '@/modules/costAnalysis/store/state';
import { IRootState } from '@/store/state';
import { ActionContext, ActionTree } from 'vuex';
import { CostAnalysisService } from '@/modules/costAnalysis/services';
import * as AT from './actionTypes';
import * as MT from './mutationsTypes';
import {
  AllAnalysisData,
  AnalysisParam,
  summaryApiParam,
  AnalysisPensionScheme,
  AnalysisFunds,
  FundAnalysisData,
  AnalysisParamWithFilter,
  FundCost,
  FundAnalysisParam,
  Paginated
} from '../types';
import OrganisationType from '../../../models/OrganisationType';
import Organisation from '../../../models/Organisation';
import TimePeriod from '../../../models/TimePeriod';
import {
  Fund,
  OrganisationManager,
  OrgFundsRequest
} from '@/modules/fund/types';
import { FETCH_ORGANISATION_SUB_FUNDS } from '../../fund/store/actionTypes';
import { SubFund } from '../../subFund/types';
import { DISPLAY_SNACKBAR } from '@/store/actionTypes';

/**
 * --------------------------------------------------
 *  Cost Analysis Actions
 * --------------------------------------------------
 **/
export const CostAnalysisActions: ICostAnalysisActions = {
  [AT.FETCH_ORGANISATION_EXPOSED_FUNDS]: (
    { commit },
    orgId: string
  ): Promise<Fund[]> => {
    return CostAnalysisService.getExposedFunds(orgId).then((data: Fund[]) => {
      commit(MT.SET_ALL_EXPOSED_FUNDS_PER_ORGANISATION, data);
      return data;
    });
  },

  [AT.FETCH_EXPOSED_PENSION_SCHEMES]: (
    { commit },
    orgId: string
  ): Promise<AnalysisPensionScheme[]> => {
    return CostAnalysisService.getExposedPensionSchemes(orgId).then(
      (data: AnalysisPensionScheme[]) => {
        commit(MT.SET_ALL_EXPOSED_PENSION_SCHEMES, data);
        return data;
      }
    );
  },

  [AT.FETCH_COST_ANALYSIS]: (
    { commit },
    params: AnalysisParamWithFilter
  ): Promise<AllAnalysisData | boolean> => {
    return CostAnalysisService.getAnalysisByFilter(params).then(
      (analysis: any) => {
        commit(MT.SET_ANALYSIS_DATA, analysis);
        return analysis;
      }
    );
  },

  [AT.FETCH_FUND_ANALYSIS]: (
    { commit },
    params: AnalysisParam
  ): Promise<FundAnalysisData | boolean> => {
    const queryParams: AnalysisParamWithFilter = {
      ...params,
      responseFilter: { fundData: true, underlyingfundData: true }
    };
    commit(MT.SET_APPLIED_FILTERS, params);
    return CostAnalysisService.getAnalysisByFilter(queryParams).then(
      (analysis: any) => {
        commit(MT.SET_FUND_ANALYSIS_DATA, analysis);
        return analysis;
      }
    );
  },

  [AT.FETCH_PAGINATED_FUND_ANALYSIS]: (
    { commit },
    params: FundAnalysisParam
  ): Promise<FundCost[]> => {
    return CostAnalysisService.getFundAnalysis(params).then(
      (analysis: Paginated<FundCost>) => {
        commit(MT.SET_PAGINATED_FUND_ANALYSIS, analysis.data);
        commit(MT.SET_FUND_ANALYSIS_PAGINATION, analysis.meta);
        return analysis.data;
      }
    );
  },

  [AT.FETCH_PAGINATED_FUNDS]: (
    { commit },
    params: OrgFundsRequest
  ): Promise<Paginated<Fund>> => {
    return CostAnalysisService.getAllFundsByFilter(params).then(
      (fundData: Paginated<Fund>) => {
        commit(MT.SET_PAGINATED_FUNDS, fundData);
        return fundData;
      }
    );
  },

  [AT.FETCH_CURRENT_FILTERS]: ({ commit, getters }): boolean => {
    const currentOrganisation: Organisation = getters.organisation;
    const exposedFunds: AnalysisFunds[] = getters.allExposedFunds;
    const exposedPensionSchemes: AnalysisPensionScheme[] =
      getters.allExposedPensionSchemes;
    const allAssetClasses: OrganisationType[] = getters.allAssetClasses;
    const assetClassIds = exposedFunds.map(it => it.product_type_id);
    const filteredAssetClasses = allAssetClasses.filter(it =>
      assetClassIds.includes(it.id)
    );
    let reportingPeriods = [];
    let exposedSubFunds: SubFund[] = [];
    if (currentOrganisation.organizationType.slug === 'fiduciary') {
      exposedSubFunds = getters.organizationSubFunds;
      reportingPeriods = exposedSubFunds.map(it => it.timePeriod);
      exposedSubFunds.sort((a, b) => {
        return a?.entity?.name?.localeCompare(b?.entity?.name);
      });
      commit(MT.SET_FILTER_SUB_FUNDS, exposedSubFunds);
    } else {
      reportingPeriods = exposedPensionSchemes.map(it => it.timePeriod);
    }
    exposedFunds.sort((a, b) => {
      return a?.name?.localeCompare(b?.name);
    });
    exposedPensionSchemes.sort((a, b) => {
      return a?.entity?.name?.localeCompare(b?.entity?.name);
    });
    commit(MT.SET_FILTER_ASSET_CLASSES, filteredAssetClasses);
    commit(MT.SET_FILTER_TIME_PERIODS, reportingPeriods);
    commit(MT.SET_FILTER_FUNDS, exposedFunds);
    commit(MT.SET_ALL_EXPOSED_FUNDS_PER_ORGANISATION, exposedFunds);
    commit(MT.SET_FILTER_PENSION_SCHEMES, exposedPensionSchemes);
    return true;
  },

  [AT.FETCH_COST_SUMMARY]: async (
    { commit },
    params
  ): Promise<AllAnalysisData> => {
    const analysis = await CostAnalysisService.getSummary(params);
    return analysis;
  },

  [AT.FETCH_ALL_ASSET_CLASSES]: ({ commit }, params): Promise<any[]> => {
    return CostAnalysisService.getAllAssetClasses(params).then(
      (response: OrganisationType[]) => {
        response.sort((a, b) => {
          return a?.name?.localeCompare(b.name);
        });
        commit(MT.SET_ALL_ASSET_CLASSES, response);
        return response;
      }
    );
  },

  [AT.FETCH_ALL_ORGANISATIONS]: (
    { commit },
    params
  ): Promise<Organisation[]> => {
    return CostAnalysisService.getAllOrganisations(params).then((data: any) => {
      commit(MT.SET_ALL_ORGANISATIONS, data);
      return data;
    });
  },

  [AT.FETCH_AN_ORGANISATION]: ({ commit }, params): Promise<Organisation> => {
    return CostAnalysisService.getOrganisationWithChildren(params).then(
      (data: any) => {
        commit(MT.SET_CURRENT_ORGANISATION, data);
        return data;
      }
    );
  },

  [AT.FETCH_ALL_ASSET_MANAGERS]: (
    { commit },
    params
  ): Promise<OrganisationManager[]> => {
    return CostAnalysisService.getOrganizationManagers(params).then(
      async (assetManagers: OrganisationManager[]) => {
        assetManagers.sort((a, b) => {
          return a?.name?.localeCompare(b?.name);
        });
        commit(MT.SET_ALL_ASSET_MANAGERS, assetManagers);
        return assetManagers;
      }
    );
  },
  [AT.FETCH_ALL_TIME_PERIODS]: ({ commit }, params): Promise<TimePeriod[]> => {
    return CostAnalysisService.getAllTimePeriods(params).then(
      (data: TimePeriod[]) => {
        data.sort((a, b) => {
          return a?.range?.localeCompare(b?.range);
        });
        commit(MT.SET_ALL_TIME_PERIODS, data);
        return data;
      }
    );
  },

  [AT.FETCH_ALL_ORGANISATION_TYPES]: ({
    commit
  }): Promise<OrganisationType[]> => {
    return CostAnalysisService.getAllOrganisationTypes().then(
      (data: OrganisationType[]) => {
        commit(MT.SET_ALL_ORGANISATION_TYPES, data);
        return data;
      }
    );
  }
};

/**
 * --------------------------------------------------
 *  Cost Analysis Action Context Interface
 * --------------------------------------------------
 **/
export interface ICostAnalysisActionContext
  extends ActionContext<ICostAnalysisState, IRootState> {}

/**
 * --------------------------------------------------
 *  Cost Analysis Actions Interface
 * --------------------------------------------------
 **/
export interface ICostAnalysisActions
  extends ActionTree<ICostAnalysisState, IRootState> {
  [AT.FETCH_COST_ANALYSIS](
    context: ICostAnalysisActionContext,
    params: AnalysisParam
  ): Promise<AllAnalysisData | boolean>;

  [AT.FETCH_CURRENT_FILTERS](context: ICostAnalysisActionContext): boolean;

  [AT.FETCH_COST_SUMMARY](
    context: ICostAnalysisActionContext,
    params: summaryApiParam
  ): Promise<AllAnalysisData>;

  [AT.FETCH_ALL_ASSET_CLASSES](
    context: ICostAnalysisActionContext,
    params: any
  ): Promise<OrganisationType[]>;

  [AT.FETCH_ALL_ORGANISATIONS](
    context: ICostAnalysisActionContext,
    params: any
  ): Promise<Organisation[]>;

  [AT.FETCH_ALL_ASSET_MANAGERS](
    context: ICostAnalysisActionContext,
    params: any
  ): Promise<OrganisationManager[]>;

  [AT.FETCH_ALL_TIME_PERIODS](
    context: ICostAnalysisActionContext,
    params: any
  ): Promise<TimePeriod[]>;
  [AT.FETCH_ALL_ORGANISATION_TYPES](
    context: ICostAnalysisActionContext
  ): Promise<OrganisationType[]>;
}
